import React from 'react';
import Icon from './Icon';

/* Figma asset: Badge variants - all colors use design tokens */
/* Figma: transfer/shopping은 orange-500, tour는 green-500.
   다국어 환경에선 children 텍스트(번역된 라벨)로 매칭할 수 없으므로
   안정적인 serviceType key('transfer'|'tour'|'shopping')로 lookup.
   legacy 호출처 호환을 위해 한국어 key도 유지. */
const serviceTypeStyles = {
  transfer: 'bg-orange-500 text-white',
  tour: 'bg-green-500 text-white',
  shopping: 'bg-orange-500 text-white',
};
const serviceStyles = {
  // legacy Korean-label matching (fallback)
  이동: 'bg-orange-500 text-white',
  투어: 'bg-green-500 text-white',
  쇼핑: 'bg-orange-500 text-white',
  '프라이빗 이동': 'bg-orange-500 text-white',
  '프라이빗 투어': 'bg-green-500 text-white',
  '프라이빗 쇼핑': 'bg-orange-500 text-white',
};

/* Figma: Status badges - outline pill style with specific colors */
const statusStyles = {
  // 예약 상태
  pending:         'border-orange-300 text-orange-300',
  confirmed:       'border-blue-500 text-blue-500',
  driver_arrived:  'border-green-500 text-green-500',
  driving:         'border-orange-500 text-orange-500',
  drive_completed: 'border-blue-700 text-blue-700',
  completed:       'border-mono-800 text-mono-800',
  cancelled:       'bg-mono-500 text-white',
  refunded:        'bg-mono-500 text-white',
  no_show:         'bg-mono-500 text-white',
  // 결제 상태
  paid:            'border-blue-500 text-blue-500',
  failed:          'border-red-500 text-red-500',
  // 문의 상태
  answered:        'border-green-500 text-green-500',
  // 대기열 상태
  waiting:         'border-orange-300 text-orange-300',
  notified:        'border-blue-500 text-blue-500',
  converted:       'border-green-500 text-green-500',
  expired:         'bg-mono-500 text-white',
  // 파트너 상태
  approved:        'border-green-500 text-green-500',
  rejected:        'border-red-500 text-red-500',
  // 투어 일정 상태
  available:       'border-green-500 text-green-500',
  fully_booked:    'border-red-500 text-red-500',
  closed:          'bg-mono-500 text-white',
  // 차량 상태
  active:          'border-green-500 text-green-500',
  maintenance:     'border-orange-300 text-orange-300',
  // 쇼핑 주문 상태
  delivered:       'border-green-500 text-green-500',
  // 노출 상태
  visible:         'border-green-500 text-green-500',
  hidden:          'border-orange-500 text-orange-500',
};

export const statusLabels = {
  // 예약 상태
  pending:         '예약 대기',
  confirmed:       '예약 확정',
  driver_arrived:  '출발지 도착',
  driving:         '운행 중',
  drive_completed: '운행 완료',
  completed:       '이용 완료',
  cancelled:       '취소',
  refunded:        '환불 완료',
  no_show:         '노쇼',
  // 결제 상태
  paid:            '결제 완료',
  failed:          '결제 실패',
  pending_pay:     '결제 대기',
  // 문의 상태
  answered:        '답변 완료',
  // 대기열 상태
  waiting:         '대기 중',
  notified:        '알림 발송',
  converted:       '예약 전환',
  expired:         '만료',
  // 파트너 상태
  approved:        '승인',
  rejected:        '거절',
  // 투어 일정 상태
  available:       '예약 가능',
  fully_booked:    '마감',
  closed:          '운영 종료',
  // 차량 상태
  active:          '운영 중',
  maintenance:     '정비 중',
  // 쇼핑 주문 상태
  delivered:       '배송 완료',
  // 노출 상태
  visible:         '노출',
  hidden:          '미노출',
};

const genericStyles = {
  level1: 'bg-mono-500 text-white',
  level2: 'bg-mono-100 text-mono-700',
  info: 'bg-blue-500 text-white',
  error: 'bg-red-500 text-white',
};

/* variant: 'service' | 'status' | 'shopping' | 'direction' | 'time' | 'generic'
   serviceType: 'transfer' | 'tour' | 'shopping' — 다국어에서 스타일 결정용 (children 번역 후에도 유효) */
export default function Badge({ children, variant = 'service', level = 'level1', icon, status, serviceType, className = '' }) {
  const base = 'inline-flex items-center justify-center w-fit rounded-badge text-2xs font-bold leading-none py-3';

  if (variant === 'service') {
    // 1순위: serviceType prop (언어 중립) / 2순위: children 한국어 라벨 / 3순위: 기본 주황
    const style = (serviceType && serviceTypeStyles[serviceType])
      || serviceStyles[children]
      || serviceTypeStyles.transfer;
    return (
      <span className={`${base} h-5 px-2 ${style} ${className}`}>
        {children}
      </span>
    );
  }

  if (variant === 'status') {
    const key = status || children;
    const isFilled = statusStyles[key]?.startsWith('bg-');
    return (
      <span className={`${base} h-5 px-2 ${isFilled ? '' : 'border bg-white'} ${statusStyles[key] || ''} ${className}`}>
        {children}
      </span>
    );
  }

  if (variant === 'shopping') {
    return (
      <span className={`${base} h-6 px-2.5 !rounded-pill text-xs font-semibold bg-green-50 text-green-700 ${className}`}>
        {children}
      </span>
    );
  }

  /* Direction badge: dark pill (출발지 → 공항 등) */
  if (variant === 'direction') {
    return (
      <span className={`inline-flex items-center justify-center px-4 h-8 bg-mono-800 text-white text-2sm font-bold rounded-pill w-fit ${className}`}>
        {children}
      </span>
    );
  }

  /* Time badge: orange pill with clock icon (무료대기, 기본 이용시간 등) */
  if (variant === 'time') {
    return (
      <span className={`inline-flex items-center gap-1 w-fit px-3 h-7 rounded-full bg-orange-50 ${className}`}>
        <Icon name={icon || 'time'} size={18} className="text-orange-600 p-0.5" />
        <span className="text-xs font-medium text-orange-600">{children}</span>
      </span>
    );
  }

  /* generic */
  return (
    <span className={`${base} h-5 px-2 ${genericStyles[level] || genericStyles.level1} ${className}`}>
      {children}
    </span>
  );
}
